import { InputNumber, Modal, Slider } from 'antd';
import React, { useEffect, useState } from 'react';

interface ClockEditorProps {
    /** null = hidden. */
    mode: 'main' | 'shot' | null;
    mainSeconds: number;
    shotSeconds: number;
    /** Upper bound for the shot clock = min(28, game clock remaining). */
    shotMax: number;
    /** Upper bound for the game clock (a full period). */
    mainMax: number;
    onSubmitMain: (seconds: number) => void;
    onSubmitShot: (seconds: number) => void;
    onCancel: () => void;
}

const splitMain = (
    totalSeconds: number
): { minutes: number; seconds: number; tenths: number; hundredths: number } => {
    const cs = Math.max(0, Math.round(totalSeconds * 100));
    return {
        minutes: Math.floor(cs / 6000),
        seconds: Math.floor((cs % 6000) / 100),
        tenths: Math.floor((cs % 100) / 10),
        hundredths: cs % 10,
    };
};

const joinMain = (minutes: number, seconds: number, tenths: number, hundredths: number): number =>
    minutes * 60 + seconds + tenths / 10 + hundredths / 100;

const ClockEditor: React.FC<ClockEditorProps> = ({
    mode,
    mainSeconds,
    shotSeconds,
    shotMax,
    mainMax,
    onSubmitMain,
    onSubmitShot,
    onCancel,
}) => {
    const [minutes, setMinutes] = useState(0);
    const [seconds, setSeconds] = useState(0);
    const [tenths, setTenths] = useState(0);
    const [hundredths, setHundredths] = useState(0);
    const [shot, setShot] = useState(0);

    useEffect(() => {
        if (mode === 'main') {
            const parts = splitMain(mainSeconds);
            setMinutes(parts.minutes);
            setSeconds(parts.seconds);
            setTenths(parts.tenths);
            setHundredths(parts.hundredths);
        } else if (mode === 'shot') {
            setShot(Math.min(Math.ceil(shotSeconds), shotMax));
        }
    }, [mode, mainSeconds, shotSeconds, shotMax]);

    const handleOk = (): void => {
        if (mode === 'main') {
            onSubmitMain(
                Math.max(0, Math.min(mainMax, joinMain(minutes, seconds, tenths, hundredths)))
            );
        } else if (mode === 'shot') {
            onSubmitShot(Math.max(0, Math.min(shotMax, shot)));
        }
    };

    return (
        <Modal
            visible={mode !== null}
            title={mode === 'main' ? 'Set game clock' : 'Set shot clock'}
            onOk={handleOk}
            onCancel={onCancel}
            okText="Set"
            destroyOnClose
        >
            {mode === 'main' && (
                <div className="m-editor">
                    <div className="m-editor__row">
                        <span className="m-editor__unit">MM</span>
                        <InputNumber
                            size="small"
                            min={0}
                            max={Math.floor(mainMax / 60)}
                            value={minutes}
                            onChange={(v): void => setMinutes(Number(v ?? 0))}
                        />
                        <span className="m-editor__unit">SS</span>
                        <InputNumber
                            size="small"
                            min={0}
                            max={59}
                            value={seconds}
                            onChange={(v): void => setSeconds(Number(v ?? 0))}
                        />
                        <span className="m-editor__unit">1/10</span>
                        <InputNumber
                            size="small"
                            min={0}
                            max={9}
                            value={tenths}
                            onChange={(v): void => setTenths(Number(v ?? 0))}
                        />
                        <span className="m-editor__unit">1/100</span>
                        <InputNumber
                            size="small"
                            min={0}
                            max={9}
                            value={hundredths}
                            onChange={(v): void => setHundredths(Number(v ?? 0))}
                        />
                    </div>
                </div>
            )}

            {mode === 'shot' && (
                <div className="m-editor">
                    <InputNumber
                        min={0}
                        max={shotMax}
                        value={shot}
                        onChange={(v): void => setShot(Number(v ?? 0))}
                    />
                    <Slider
                        min={0}
                        max={shotMax}
                        value={shot > shotMax ? shotMax : shot}
                        onChange={(v): void => setShot(v as number)}
                    />
                    <div className="m-editor__hint">
                        Max {shotMax}s (cannot exceed the game clock).
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default ClockEditor;
